import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Star, CheckCircle } from '@phosphor-icons/react';
import { ratingAPI } from '../lib/api';
import RatingDialog from './RatingDialog';

const SAT_LABEL = {
  muy_satisfecho: 'Muy satisfactorio',
  satisfecho: 'Satisfactorio',
  poco_satisfecho: 'Poco satisfactorio',
  nada_satisfecho: 'Nada satisfactorio',
};

/**
 * Full rating card shown after the contract is live (chat_available=true).
 * Handles three states:
 *  - Not rated yet  -> CTA to open the dialog
 *  - Already rated  -> shows the submitted rating
 *  - Not allowed    -> hidden
 */
const RatingCard = ({ quotationId, role, counterpartName }) => {
  const [state, setState] = useState({ loading: true, canRate: false, alreadyRated: false, rating: null, toUserId: null });
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await ratingAPI.canRate(quotationId);
      setState({
        loading: false,
        canRate: !!data.can_rate,
        alreadyRated: !!data.already_rated,
        rating: data.rating || null,
        toUserId: data.to_user_id || null,
      });
    } catch (_e) {
      setState({ loading: false, canRate: false, alreadyRated: false, rating: null, toUserId: null });
    }
  }, [quotationId]);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return null;
  if (!state.canRate && !state.alreadyRated) return null;

  const targetLabel = role === 'cliente' ? 'Proveedor' : 'Cliente';

  return (
    <>
      <Card className="border-amber-200 dark:border-amber-800 rounded-none" data-testid="rating-card">
        <CardHeader className="border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <CardTitle className="text-base flex items-center gap-2 text-amber-800 dark:text-amber-300">
            <Star className="w-5 h-5" weight="fill" />
            Califica al {targetLabel}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-3">
          {state.alreadyRated && state.rating ? (
            <>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" weight="fill" />
                <p className="text-sm font-medium text-green-700 dark:text-green-400">Ya enviaste tu calificación</p>
              </div>
              <div className="border border-zinc-200 dark:border-zinc-700 p-3 space-y-2 bg-white dark:bg-zinc-900">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`w-5 h-5 ${n <= state.rating.stars ? 'text-amber-400' : 'text-zinc-300 dark:text-zinc-600'}`}
                      weight={n <= state.rating.stars ? 'fill' : 'regular'}
                    />
                  ))}
                  <span className="ml-2 text-sm font-medium">{state.rating.stars} / 5</span>
                </div>
                {state.rating.satisfaction && (
                  <Badge variant="outline" className="rounded-none text-xs">
                    {SAT_LABEL[state.rating.satisfaction] || state.rating.satisfaction}
                  </Badge>
                )}
                {state.rating.comment && (
                  <p className="text-sm text-zinc-700 dark:text-zinc-300 italic">"{state.rating.comment}"</p>
                )}
              </div>
              <div className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="text-xs text-amber-800 dark:text-amber-300 font-medium mb-1">
                  El chat se cerró al calificar
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                  Los datos de contacto del {targetLabel.toLowerCase()} ya no son visibles. Puedes consultar
                  el historial del chat pero no enviar nuevos mensajes. La calificación es permanente.
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-zinc-700 dark:text-zinc-300">
                La transacción está completada. Comparte tu experiencia con <strong>{counterpartName || `el ${targetLabel.toLowerCase()}`}</strong>.
                Tu calificación ayuda a mantener la confianza de la comunidad MaterMatch.
              </p>
              <Button onClick={() => setDialogOpen(true)} className="btn-primary rounded-none" data-testid="open-rating-dialog">
                <Star className="w-4 h-4 mr-2" weight="fill" />
                Calificar ahora
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <RatingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        quotationId={quotationId}
        toUserId={state.toUserId}
        toUserName={counterpartName}
        role={role}
        onSubmitted={load}
      />
    </>
  );
};

export default RatingCard;
